import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDsTheme } from '../../design/ThemeProvider';
import Blueprint2D from './Blueprint2D';
import BlueprintLogo from './BlueprintLogo';
import EngineScene3D from './engine3d/EngineScene3D';
import { Act } from './engine3d/choreography';
import LoaderBoundary from './LoaderBoundary';
import SignageLoader from './SignageLoader';
import './startupLoader.css';

/*
 * Startup gate shell. Renders the 3D turbofan (engine3d/) that builds itself,
 * explodes and reassembles - or the 2D blueprint (Blueprint2D) when motion is
 * reduced, WebGL2 is unavailable, or the GPU cannot keep up.
 *
 * The shell owns what both share: the caption (the live region), the amber
 * progress rule, the tagline, the dev hold flag and the final 200ms fade.
 * A renderer gets `finishing` once data is in, closes the engine up from
 * wherever it is, and calls `onSettled`; the shell then fades and calls
 * `onDone`. Worst case that adds ~1.2s after `ready`.
 *
 * The loader must never stand between a user and the app. Three independent
 * guarantees, each enough on its own:
 *  - the renderers fall back (3D -> 2D) instead of throwing;
 *  - a watchdog fades out MAX_SETTLE_MS after `ready` even if no renderer
 *    ever settles (background tabs pause requestAnimationFrame, GPUs hang);
 *  - LoaderBoundary catches any render error and lets the app in.
 */

interface Props {
  /** Current real startup step, announced to screen readers. */
  statusText: string;
  /** Startup data is in; finish the current move, fade out, call onDone. */
  ready: boolean;
  /** Startup data failed to load; show Retry instead of waiting forever. */
  error?: boolean;
  onRetry?: () => void;
  onDone?: () => void;
}

const FADE_MS = 200;
/** Hard deadline from `ready` to fade-out, whatever the renderer is doing. */
const MAX_SETTLE_MS = 2500;

const osReducedMotion = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Dev-only design-review aid: `#/?startup-loader=hold` keeps the loader up. */
const holdFlag = () =>
  process.env.NODE_ENV === 'development' && /[?&]startup-loader=hold\b/.test(window.location.hash);

const hasWebGL2 = () => typeof window.WebGL2RenderingContext !== 'undefined';

const SIGNAGE_KEY = 'maintener.signage';
/**
 * Yodeck signage players can't afford the animated loader; they get the
 * static SignageLoader. Remembered for the tab so an MSAL redirect round-trip
 * (which lands on the redirect URI, without "yodeck") doesn't start the canvas.
 */
const isSignage = () => {
  const inUrl = /yodeck/i.test(window.location.href);
  try {
    if (inUrl) window.sessionStorage.setItem(SIGNAGE_KEY, '1');
    return inUrl || window.sessionStorage.getItem(SIGNAGE_KEY) === '1';
  } catch {
    return inUrl;
  }
};

const StartupLoaderInner = ({ statusText, ready, error, onRetry, onDone }: Props) => {
  const { reduceMotion } = useDsTheme();
  const [reduced] = useState(() => reduceMotion || osReducedMotion());
  const [hold] = useState(holdFlag);
  const [mode, setMode] = useState<'3d' | '2d'>(() => (reduced || !hasWebGL2() ? '2d' : '3d'));
  const [started, setStarted] = useState(false);
  const [out, setOut] = useState(false);
  const [act, setAct] = useState<Act>('build');
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const finishing = ready && !hold;

  // Kick the progress rule off after the first paint so it has a start value.
  useEffect(() => {
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setStarted(true));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, []);

  // Watchdog: renderers normally settle in ~1.2s; never wait longer than this.
  useEffect(() => {
    if (!finishing) return;
    const t = window.setTimeout(() => setOut(true), MAX_SETTLE_MS);
    return () => window.clearTimeout(t);
  }, [finishing]);

  useEffect(() => {
    if (!out) return;
    const t = window.setTimeout(() => onDoneRef.current?.(), FADE_MS);
    return () => window.clearTimeout(t);
  }, [out]);

  const onSettled = useCallback(() => setOut(true), []);
  const onFallback = useCallback(() => setMode('2d'), []);

  return (
    <div
      className="sl-root"
      data-out={out || undefined}
      data-started={started || undefined}
      data-ready={finishing || undefined}
      data-error={error || undefined}
    >
      <BlueprintLogo />
      {mode === '3d' ? (
        <div className="sl-stage sl-stage--3d" data-phase={act}>
          <div className="sl-glow sl-glow--dim" aria-hidden="true" />
          <div className="sl-glow sl-glow--bright" aria-hidden="true" />
          <EngineScene3D finishing={finishing} onSettled={onSettled} onAct={setAct} onFallback={onFallback} />
        </div>
      ) : (
        <Blueprint2D reduced={reduced} finishing={finishing} onSettled={onSettled} />
      )}
      <div className="sl-footer">
        <div className="sl-brand" aria-hidden="true">MAINTENER</div>
        <div className="sl-caption" role="status" aria-live="polite">{statusText}</div>
        <div className="sl-progress" aria-hidden="true"><div className="sl-progress-bar" /></div>
        {error && onRetry && (
          <button type="button" className="sl-retry" onClick={onRetry}>Retry</button>
        )}
      </div>
    </div>
  );
};

const StartupLoader = (props: Props) => {
  // onDone must fire exactly once, whichever path (normal fade or boundary) gets there.
  const [signage] = useState(isSignage);
  const doneRef = useRef(false);
  const onDoneRef = useRef(props.onDone);
  onDoneRef.current = props.onDone;
  const onDone = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    onDoneRef.current?.();
  }, []);

  return (
    <LoaderBoundary
      statusText={props.statusText}
      ready={props.ready}
      error={props.error}
      onRetry={props.onRetry}
      onDone={onDone}
    >
      {signage
        ? <SignageLoader {...props} onDone={onDone} />
        : <StartupLoaderInner {...props} onDone={onDone} />}
    </LoaderBoundary>
  );
};

export default StartupLoader;
